/**
 * Hooks React Query da Tela 2 (Pessoas) — Brief 2B B2.
 *
 * Espelha o padrão de `hooks/use-conversations.ts` (queryKey por params,
 * staleTime 30s, refetch manual). Read-only — sem mutations no v1.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../../lib/api-client';
import { personWaitlistQuery } from '../../lib/person-waitlist-query';

import type {
  ListPersonsResponse,
  PersonsListFilters,
  PersonDetail,
  ListPersonConversationsResponse,
  ListPersonAppointmentsResponse,
} from './types';
import type {
  BondParticipantWireItem,
  PortfolioEntryWireItem,
  WaitlistEntryWire,
} from '../../lib/api-client';
import type { UseMutationResult } from '@tanstack/react-query';

const STALE = 30 * 1000;

export function usePersons(
  filters: PersonsListFilters,
  enabled = true,
  opts?: { keepPreviousData?: boolean },
): {
  data: ListPersonsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ['persons', filters],
    queryFn: () => api.listPersons(filters),
    staleTime: STALE,
    // Alvo A round2 — a busca da Inbox gateia o FETCH (cap person-read),
    // não só o render (regra «gatear o fetch»). Default true = retrocompat.
    enabled,
    // Opt-in (busca da Inbox): resultado anterior fica na tela entre teclas
    // (mata o flicker de key nova → data undefined). Default: comportamento
    // histórico dos demais callers.
    ...(opts?.keepPreviousData === true && { placeholderData: keepPreviousData }),
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

export function usePerson(personId: string | undefined): {
  data: PersonDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ['person', personId],
    queryFn: () => api.getPerson(personId ?? ''),
    enabled: personId !== undefined && personId !== '',
    staleTime: STALE,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

export function usePersonConversations(
  personId: string | undefined,
  enabled = true,
): {
  data: ListPersonConversationsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: ['person-conversations', personId],
    queryFn: () => api.listPersonConversations(personId ?? '', { limit: 20 }),
    enabled: enabled && personId !== undefined && personId !== '',
    staleTime: STALE,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

export function usePersonAppointments(
  personId: string | undefined,
  enabled = true,
): {
  data: ListPersonAppointmentsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: ['person-appointments', personId],
    queryFn: () => api.listPersonAppointments(personId ?? '', { limit: 20 }),
    enabled: enabled && personId !== undefined && personId !== '',
    staleTime: STALE,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

/**
 * Frente B Sessão 3 — fila(s) de espera VIVAS desta pessoa, na ficha 360º.
 * Reusa o endpoint `/v1/scheduling/waitlist?personId=` (filtro adicionado na Sessão 2).
 * **Gateia o fetch** por `enabled` (a cap `scheduling-waitlist`, decidida pelo caller) +
 * personId → zero query fora de tenant com fila. 403 → vazio (defensivo).
 */
export function usePersonWaitlist(
  personId: string | undefined,
  enabled = true,
): {
  data: WaitlistEntryWire[];
  isLoading: boolean;
  isError: boolean;
} {
  // Key+fn compartilhadas com o painel da Conversa em curso (contrato de cache —
  // vide docblock de `personWaitlistQuery`).
  const query = useQuery({
    ...personWaitlistQuery(personId),
    enabled: enabled && personId !== undefined && personId !== '',
    staleTime: STALE,
  });
  return { data: query.data ?? [], isLoading: query.isLoading, isError: query.isError };
}

/**
 * Frente B Plano 2 (setup) peça 4 — EDITAR a autonomia de agenda por-cliente na ficha.
 * PATCH no BondParticipant EXISTENTE (`updateBondParticipant`, If-Match com a version do
 * participant). Em sucesso invalida `['person', personId]` (a ficha refaz o fetch e o
 * `BondsSection` reflete o novo modo). `null` limpa o override (volta a herdar o cascade).
 */
export function useUpdateBondAutonomy(personId: string): UseMutationResult<
  BondParticipantWireItem,
  Error,
  {
    bondId: string;
    participantId: string;
    version: number;
    nativeSchedulingMode: string | null;
    waitlistPreferred: boolean | null;
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p) =>
      api.updateBondParticipant(p.bondId, p.participantId, p.version, {
        nativeSchedulingMode: p.nativeSchedulingMode,
        waitlistPreferred: p.waitlistPreferred,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', personId] });
    },
  });
}

/**
 * S4 PR-b (Corrente de Compromissos, Item 404 faceta b) — EDITAR a política de
 * atuação da IA por-cliente na ficha. PATCH no BondParticipant EXISTENTE
 * (molde `useUpdateBondAutonomy`). Em sucesso invalida `['person', personId]`.
 * `null` limpa o override (volta a herdar `'engage'`).
 */
export function useUpdateBondEngagement(
  personId: string,
): UseMutationResult<
  BondParticipantWireItem,
  Error,
  { bondId: string; participantId: string; version: number; aiEngagementMode: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p) =>
      api.updateBondParticipant(p.bondId, p.participantId, p.version, {
        aiEngagementMode: p.aiEngagementMode,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', personId] });
    },
  });
}

/**
 * S4 PR-b — a carteira (Item 377) DESTE cliente na ficha 360º. Reusa
 * `GET /v1/customer-portfolio?personId=` (filtro aditivo desta PR).
 * **Gateia o fetch** por `enabled` (a cap `customer-portfolio-read`, decidida
 * pelo caller) — 403 → vazio (defensivo, molde `usePersonWaitlist`).
 * `tenantId_personId` é `@@unique` no Engine e o fetch é ACTIVE-ONLY:
 * no máximo 1 linha, e ela é o dono ATUAL (linha inativa = sem carteira).
 */
export function usePersonPortfolio(
  personId: string | undefined,
  enabled = true,
): {
  data: PortfolioEntryWireItem | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ['person-portfolio', personId],
    queryFn: async () => {
      try {
        // Só carteira ATIVA (default do endpoint): linha inativa (removida no
        // passado / recém-removida) NÃO é "dono atual" — includeInactive aqui
        // faria a ficha mentir e o «Remover» virar loop (revisão adversarial B1).
        // Consistente com a Corrente/Inbox (enrichment filtra active).
        const res = await api.listCustomerPortfolio({ personId: personId ?? '' });
        return res.data[0] ?? null;
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) return null;
        throw err;
      }
    },
    enabled: enabled && personId !== undefined && personId !== '',
    staleTime: 30 * 1000,
  });
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/** S4 PR-b — atribuir/reatribuir o cliente a um operador (idempotente por personId). */
export function useAssignPortfolio(
  personId: string,
): UseMutationResult<{ data: PortfolioEntryWireItem }, Error, { operatorUserId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p) => api.assignCustomerPortfolio({ personId, operatorUserId: p.operatorUserId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person-portfolio', personId] });
    },
  });
}

/** S4 PR-b — remover o cliente da carteira (soft-deactivate, If-Match). */
export function useRemovePortfolio(
  personId: string,
): UseMutationResult<void, Error, { id: string; version: number }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p) => api.removeCustomerPortfolio(p.id, p.version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person-portfolio', personId] });
    },
  });
}
